/**
 * Datastores API
 * Manages datastore links for agents
 */

import { BaseApiClient } from "./base";

// ========== Types ==========

export type AccessMode = "read" | "write" | "read_write";

export interface Datastore {
  id: string;
  workspace_id: string;
  key: string;
  title: string;
}

export interface AgentDatastore {
  id: string;
  workspace_id: string;
  agent_configuration_id: string;
  datastore_id: string;
  title: string;
  access_mode: AccessMode;
  created_at: string;
  updated_at: string;
}

export interface CreateAgentDatastoreRequest {
  agentConfigurationId: string;
  datastoreId: string;
  title?: string;
  accessMode?: AccessMode;
}

export interface UpdateAgentDatastoreRequest {
  title?: string;
  accessMode?: AccessMode;
}

// ========== API Client ==========

class DatastoresApiClient extends BaseApiClient {
  /**
   * Get catalog of available datastores
   */
  async getCatalog(): Promise<Datastore[]> {
    return this.request<Datastore[]>("/datastores/catalog");
  }

  /**
   * List datastores linked to an agent configuration
   */
  async list(agentConfigurationId: string): Promise<AgentDatastore[]> {
    return this.request<AgentDatastore[]>(`/datastores?agentConfigurationId=${agentConfigurationId}`);
  }

  /**
   * Create a new agent datastore link
   */
  async create(data: CreateAgentDatastoreRequest): Promise<AgentDatastore> {
    return this.request<AgentDatastore>("/datastores", {
      method: "POST",
      body: JSON.stringify(data)
    });
  }

  /**
   * Update an agent datastore
   */
  async update(id: string, data: UpdateAgentDatastoreRequest): Promise<AgentDatastore> {
    return this.request<AgentDatastore>(`/datastores/${id}`, {
      method: "PUT",
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete an agent datastore link
   */
  async delete(id: string): Promise<{ success: boolean; id: string }> {
    return this.request<{ success: boolean; id: string }>(`/datastores/${id}`, {
      method: "DELETE"
    });
  }
}

export const datastoresApi = new DatastoresApiClient();
